import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { 
  Scale,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Lightbulb,
  CheckCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Eye,
  MessageSquare,
  Camera,
  RefreshCw,
  Battery,
  Utensils,
  Dumbbell,
  Moon,
  Brain,
  Zap
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mini chart component
const MiniChart = ({ data, color, label }) => {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[#888] text-xs">{label}</span>
        <span className={cn("font-bold", color)}>{values[values.length - 1]}</span>
      </div>
      <div className="h-16 flex items-end gap-1">
        {data.map((d, i) => {
          const height = ((d.value - min) / range) * 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div 
                className={cn("w-full rounded-t", color.replace('text-', 'bg-'))}
                style={{ height: `${Math.max(height, 10)}%`, opacity: 0.3 + (i / data.length) * 0.7 }}
              />
              <span className="text-[8px] text-[#555]">{d.week}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Photo comparison slider
const PhotoComparison = ({ before, after, label }) => {
  const [position, setPosition] = useState(50);
  
  if (!before && !after) return null;
  
  return (
    <div className="space-y-2">
      <span className="text-[#888] text-xs">{label}</span>
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-[#1A1A1A]">
        {after && (
          <img src={after} alt="Después" className="absolute inset-0 w-full h-full object-cover" />
        )}
        {before && (
          <div 
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${position}%` }}
          >
            <img src={before} alt="Antes" className="w-full h-full object-cover" style={{ width: `${10000/position}%` }} />
          </div>
        )}
        {before && after && (
          <>
            <div 
              className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg"
              style={{ left: `${position}%` }}
            />
            <input
              type="range"
              min="10"
              max="90"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
            />
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-[10px] text-white">
              Antes
            </div>
            <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded text-[10px] text-white">
              Después
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Alert component
const AlertCard = ({ alert }) => {
  const icons = {
    stagnation: Scale,
    rapid_loss: TrendingDown,
    low_energy: Battery,
    high_hunger: Utensils,
    low_adherence: AlertTriangle
  };
  const Icon = icons[alert.type] || AlertTriangle;
  
  const severityColors = {
    warning: "border-yellow-500/30 bg-yellow-500/5",
    caution: "border-orange-500/30 bg-orange-500/5",
    info: "border-blue-500/30 bg-blue-500/5"
  };
  
  return (
    <div className={cn(
      "p-3 rounded-lg border",
      severityColors[alert.severity] || severityColors.info
    )}>
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-white text-sm font-medium">{alert.title}</h4>
          <p className="text-[#888] text-xs mt-1">{alert.message}</p>
        </div>
      </div>
    </div>
  );
};

// Suggestion component
const SuggestionCard = ({ suggestion }) => {
  return (
    <div className="p-3 rounded-lg border border-[#D4AF37]/20 bg-[#D4AF37]/5">
      <div className="flex items-start gap-3">
        <Lightbulb className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-white text-sm font-medium">{suggestion.title}</h4>
          <p className="text-[#888] text-xs mt-1">{suggestion.message}</p>
        </div>
      </div>
    </div>
  );
};

// Check-in card
const CheckInCard = ({ checkIn, onReview, isSelected }) => {
  const date = new Date(checkIn.date || checkIn.created_at);
  
  return (
    <div 
      className={cn(
        "p-4 rounded-lg border transition-all cursor-pointer",
        isSelected 
          ? "border-[#D4AF37] bg-[#D4AF37]/5" 
          : checkIn.coach_reviewed 
            ? "border-[#2A2A2A] bg-[#1A1A1A] opacity-70"
            : "border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#3A3A3A]"
      )}
      onClick={() => onReview(checkIn)}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#666]" />
          <span className="text-white text-sm font-medium">
            Semana {checkIn.week}
          </span>
        </div>
        {checkIn.coach_reviewed ? (
          <span className="px-2 py-0.5 text-[10px] bg-green-500/10 text-green-400 rounded">
            Revisado
          </span>
        ) : (
          <span className="px-2 py-0.5 text-[10px] bg-yellow-500/10 text-yellow-400 rounded">
            Pendiente
          </span>
        )}
      </div>
      
      <div className="grid grid-cols-4 gap-2 text-center">
        <div>
          <Scale className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" />
          <span className="text-white text-sm font-bold">{checkIn.weight || '-'}</span>
          <span className="text-[#666] text-[10px] block">kg</span>
        </div>
        <div>
          <Activity className="w-4 h-4 text-green-400 mx-auto mb-1" />
          <span className="text-white text-sm font-bold">{checkIn.energy_level || '-'}</span>
          <span className="text-[#666] text-[10px] block">Energía</span>
        </div>
        <div>
          <Utensils className="w-4 h-4 text-orange-400 mx-auto mb-1" />
          <span className="text-white text-sm font-bold">{checkIn.diet_adherence || '-'}%</span>
          <span className="text-[#666] text-[10px] block">Dieta</span>
        </div>
        <div>
          <Dumbbell className="w-4 h-4 text-purple-400 mx-auto mb-1" />
          <span className="text-white text-sm font-bold">{checkIn.training_adherence || '-'}%</span>
          <span className="text-[#666] text-[10px] block">Entreno</span>
        </div>
      </div>
      
      {(checkIn.photos?.front || checkIn.photos?.side || checkIn.photos?.back) && (
        <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[#2A2A2A]">
          <Camera className="w-3 h-3 text-[#666]" />
          <span className="text-[#666] text-[10px]">
            {[checkIn.photos?.front, checkIn.photos?.side, checkIn.photos?.back].filter(Boolean).length} fotos
          </span>
        </div>
      )}
    </div>
  );
};

// Main Component
const ClientCheckInsView = ({ memberId, memberName }) => {
  const [checkIns, setCheckIns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [intelligence, setIntelligence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [coachNotes, setCoachNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders } = useAuth();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [checkInsRes, analyticsRes, intelligenceRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/members/${memberId}/check-ins`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/members/${memberId}/check-ins/analytics`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/members/${memberId}/check-ins/intelligence`, { headers: getAuthHeaders() })
      ]);
      
      setCheckIns(checkInsRes.data.check_ins || []);
      setAnalytics(analyticsRes.data);
      setIntelligence(intelligenceRes.data);
    } catch (error) {
      console.error("Error loading check-ins:", error);
    } finally {
      setLoading(false);
    }
  }, [memberId, getAuthHeaders]);

  useEffect(() => {
    if (memberId) loadData();
  }, [memberId, loadData]);

  const handleReview = (checkIn) => {
    setSelectedCheckIn(checkIn);
    setCoachNotes(checkIn.coach_notes || "");
    setShowReviewDialog(true);
  };

  const handleSaveReview = async () => {
    if (!selectedCheckIn) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/members/${memberId}/check-ins/${selectedCheckIn.id}/review`,
        { coach_notes: coachNotes },
        { headers: getAuthHeaders() }
      );
      toast.success("Check-in revisado");
      setShowReviewDialog(false);
      loadData();
    } catch (error) {
      toast.error("Error al guardar revisión");
    } finally {
      setSaving(false);
    }
  };

  // Get first and last check-ins for comparison
  const firstCheckIn = checkIns[checkIns.length - 1];
  const lastCheckIn = checkIns[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (checkIns.length === 0) {
    return (
      <div className="text-center py-12">
        <Scale className="w-12 h-12 text-[#333] mx-auto mb-4" />
        <h3 className="text-white font-medium mb-2">Sin check-ins</h3>
        <p className="text-[#666] text-sm">Este cliente aún no ha enviado check-ins semanales</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Intelligence Alerts & Suggestions */}
      {intelligence && (intelligence.alerts?.length > 0 || intelligence.suggestions?.length > 0) && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="text-white font-semibold">Análisis Inteligente</h3>
          </div>
          
          {intelligence.alerts?.map((alert, i) => (
            <AlertCard key={i} alert={alert} />
          ))}
          
          {intelligence.suggestions?.map((suggestion, i) => (
            <SuggestionCard key={i} suggestion={suggestion} />
          ))}
        </div>
      )}

      {/* Weight Trend Summary */}
      {analytics?.weight_trend && (
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold">Progreso de Peso</h3>
            <div className={cn(
              "flex items-center gap-1 px-2 py-1 rounded",
              analytics.weight_trend.direction === 'down' ? "bg-green-500/10 text-green-400" :
              analytics.weight_trend.direction === 'up' ? "bg-red-500/10 text-red-400" :
              "bg-[#2A2A2A] text-[#888]"
            )}>
              {analytics.weight_trend.direction === 'down' ? <TrendingDown className="w-4 h-4" /> :
               analytics.weight_trend.direction === 'up' ? <TrendingUp className="w-4 h-4" /> :
               <Minus className="w-4 h-4" />}
              <span className="font-bold">
                {analytics.weight_trend.change > 0 ? '+' : ''}{analytics.weight_trend.change}kg
              </span>
            </div>
          </div>
          
          {/* Mini weight chart */}
          <MiniChart 
            data={analytics.weight} 
            color="text-[#D4AF37]" 
            label="Peso (kg)"
          />
        </div>
      )}

      {/* Photo Comparison */}
      {firstCheckIn && lastCheckIn && (firstCheckIn.photos?.front || lastCheckIn.photos?.front) && (
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
          <h3 className="text-white font-semibold mb-4">Comparación Visual</h3>
          <div className="grid grid-cols-3 gap-3">
            <PhotoComparison 
              before={firstCheckIn.photos?.front}
              after={lastCheckIn.photos?.front}
              label="Frontal"
            />
            <PhotoComparison 
              before={firstCheckIn.photos?.side}
              after={lastCheckIn.photos?.side}
              label="Lateral"
            />
            <PhotoComparison 
              before={firstCheckIn.photos?.back}
              after={lastCheckIn.photos?.back}
              label="Espalda"
            />
          </div>
          <p className="text-[#555] text-[10px] text-center mt-3">
            Desliza para comparar: Semana {firstCheckIn.week} → Semana {lastCheckIn.week}
          </p>
        </div>
      )}

      {/* Adherence & Energy Charts */}
      {analytics && (
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
            <MiniChart 
              data={analytics.energy} 
              color="text-green-400" 
              label="Energía"
            />
          </div>
          <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
            <div className="space-y-2">
              <span className="text-[#888] text-xs">Adherencia Media</span>
              {analytics.adherence?.length > 0 && (
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-[#D4AF37] text-lg font-bold">
                      {Math.round(analytics.adherence.reduce((a, b) => a + b.diet, 0) / analytics.adherence.length)}%
                    </span>
                    <span className="text-[#666] text-xs block">Dieta</span>
                  </div>
                  <div>
                    <span className="text-purple-400 text-lg font-bold">
                      {Math.round(analytics.adherence.reduce((a, b) => a + b.training, 0) / analytics.adherence.length)}%
                    </span>
                    <span className="text-[#666] text-xs block">Entreno</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Check-ins List */}
      <div>
        <h3 className="text-white font-semibold mb-3">Historial de Check-ins</h3>
        <div className="space-y-3">
          {checkIns.map(ci => (
            <CheckInCard 
              key={ci.id} 
              checkIn={ci} 
              onReview={handleReview}
              isSelected={selectedCheckIn?.id === ci.id}
            />
          ))}
        </div>
      </div>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">
              Revisar Check-in - Semana {selectedCheckIn?.week}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCheckIn && (
            <div className="space-y-4">
              {/* Metrics summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-[#1A1A1A] rounded-lg">
                  <div className="text-[#666] text-xs mb-1">Peso</div>
                  <div className="text-[#D4AF37] text-xl font-bold">{selectedCheckIn.weight}kg</div>
                </div>
                <div className="p-3 bg-[#1A1A1A] rounded-lg">
                  <div className="text-[#666] text-xs mb-1">Energía</div>
                  <div className="text-green-400 text-xl font-bold">{selectedCheckIn.energy_level}/10</div>
                </div>
                <div className="p-3 bg-[#1A1A1A] rounded-lg">
                  <div className="text-[#666] text-xs mb-1">Hambre</div>
                  <div className="text-orange-400 text-xl font-bold">{selectedCheckIn.hunger_level}/10</div>
                </div>
                <div className="p-3 bg-[#1A1A1A] rounded-lg">
                  <div className="text-[#666] text-xs mb-1">Sueño</div>
                  <div className="text-blue-400 text-xl font-bold">{selectedCheckIn.sleep_quality}/10</div>
                </div>
              </div>

              {/* Adherence */}
              <div className="p-3 bg-[#1A1A1A] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#888] text-xs">Adherencia</span>
                </div>
                <div className="flex gap-4">
                  <div>
                    <span className="text-[#D4AF37] font-bold">{selectedCheckIn.diet_adherence}%</span>
                    <span className="text-[#666] text-xs ml-1">Dieta</span>
                  </div>
                  <div>
                    <span className="text-purple-400 font-bold">{selectedCheckIn.training_adherence}%</span>
                    <span className="text-[#666] text-xs ml-1">Entreno</span>
                  </div>
                </div>
              </div>

              {/* Client notes */}
              {selectedCheckIn.client_notes && (
                <div className="p-3 bg-[#1A1A1A] rounded-lg">
                  <div className="text-[#888] text-xs mb-2">Notas del cliente:</div>
                  <p className="text-white text-sm">{selectedCheckIn.client_notes}</p>
                </div>
              )}

              {/* Photos */}
              {(selectedCheckIn.photos?.front || selectedCheckIn.photos?.side || selectedCheckIn.photos?.back) && (
                <div className="grid grid-cols-3 gap-2">
                  {selectedCheckIn.photos.front && (
                    <img src={selectedCheckIn.photos.front} alt="Front" className="rounded-lg aspect-[3/4] object-cover" />
                  )}
                  {selectedCheckIn.photos.side && (
                    <img src={selectedCheckIn.photos.side} alt="Side" className="rounded-lg aspect-[3/4] object-cover" />
                  )}
                  {selectedCheckIn.photos.back && (
                    <img src={selectedCheckIn.photos.back} alt="Back" className="rounded-lg aspect-[3/4] object-cover" />
                  )}
                </div>
              )}

              {/* Coach notes */}
              <div>
                <label className="text-[#888] text-xs block mb-2">Notas del coach</label>
                <Textarea
                  value={coachNotes}
                  onChange={(e) => setCoachNotes(e.target.value)}
                  placeholder="Añade observaciones, recomendaciones..."
                  className="bg-[#1A1A1A] border-[#2A2A2A] text-white min-h-[100px]"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowReviewDialog(false)}
                  className="flex-1 border-[#2A2A2A] text-white"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveReview}
                  disabled={saving}
                  className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-[#0B0B0B]"
                >
                  {saving ? "Guardando..." : "Marcar Revisado"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientCheckInsView;
